import React from 'react'
import { Link, useParams } from 'react-router-dom'
import { TrendingUp, Truck, Package, FileText, Mail, History, Building, User, Phone, ChevronRight, Download, RefreshCw, Loader2 } from 'lucide-react'
import { usePurchaseOrder } from '@/hooks/procurement'
import { Loading, InlineLoading } from '@/components/Loading'
import { InlineError } from '@/components/ErrorDisplay'
import { PURCHASE_STATUS_LABELS, type PurchaseOrderStatus } from '@/api/types/procurement'

function PurchaseDetail() {
  const { id } = useParams()
  const orderId = id ? parseInt(id) : 0

  const { data: purchaseOrder, isLoading, error } = usePurchaseOrder(orderId)

  // Timeline steps based on purchase order status
  const getTimelineSteps = (status: PurchaseOrderStatus) => {
    const steps = [
      { key: 'created', label: '已创建', status: 'completed' as const, icon: '✓' },
      { key: 'approved', label: '已审批', status: 'pending' as const, time: '', icon: '✓' },
      { key: 'ordered', label: '已下单', status: 'pending' as const, time: '', icon: '📦' },
      { key: 'shipping', label: '运输中', status: 'pending' as const, time: '', icon: '🚚' },
      { key: 'receiving', label: '已到货', status: 'pending' as const, time: '', icon: '✓' },
    ]

    let currentStepIndex = 0
    switch (status) {
      case 'CREATED':
        currentStepIndex = 0
        break
      case 'APPROVED':
        currentStepIndex = 1
        break
      case 'ORDERED':
        currentStepIndex = 2
        break
      case 'IN_TRANSIT':
      case 'PARTIALLY_RECEIVED':
        currentStepIndex = 3
        break
      case 'RECEIVED':
      case 'CLOSED':
        currentStepIndex = 4
        break
      default:
        currentStepIndex = 0
    }

    return steps.map((step, index) => ({
      ...step,
      status: index < currentStepIndex ? 'completed' : index === currentStepIndex ? 'completed' : 'pending',
      current: index === currentStepIndex,
    }))
  }

  const statusMap: Record<PurchaseOrderStatus, { label: string; class: string }> = {
    CREATED: { label: '待审批', class: 'bg-amber-100 text-amber-600' },
    APPROVED: { label: '已审批', class: 'bg-blue-100 text-blue-600' },
    ORDERED: { label: '已下单', class: 'bg-cyan-100 text-cyan-600' },
    IN_TRANSIT: { label: '运输中', class: 'bg-purple-100 text-purple-600' },
    PARTIALLY_RECEIVED: { label: '部分到货', class: 'bg-orange-100 text-orange-600' },
    RECEIVED: { label: '已到货', class: 'bg-green-100 text-green-600' },
    CANCELLED: { label: '已取消', class: 'bg-red-100 text-red-600' },
    CLOSED: { label: '已关闭', class: 'bg-gray-100 text-gray-600' },
  }

  if (isLoading) {
    return (
      <div className="p-6">
        <Loading text="加载采购单详情..." />
      </div>
    )
  }

  if (error || !purchaseOrder) {
    return (
      <div className="p-6">
        <InlineError message="加载失败，请重试" />
      </div>
    )
  }

  const timelineSteps = getTimelineSteps(purchaseOrder.status)

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/supply-chain/purchase/list" className="p-2 hover:bg-gray-100 rounded-lg transition">
              <TrendingUp className="w-5 h-5 text-gray-600 rotate-180" />
            </Link>
            <div>
              <h1 className="text-xl font-bold text-gray-900">采购单详情</h1>
              <p className="text-sm text-gray-500 mt-1">{purchaseOrder.purchaseNo}</p>
            </div>
          </div>
          <span
            className={`px-3 py-1 rounded-full text-sm font-medium ${statusMap[purchaseOrder.status as PurchaseOrderStatus]?.class || ''}`}
          >
            {statusMap[purchaseOrder.status as PurchaseOrderStatus]?.label || purchaseOrder.status}
          </span>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-6">
        {/* Left: Main Info */}
        <div className="col-span-2 space-y-6">
          {/* Status Timeline */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100">
            <div className="p-5 border-b border-gray-100">
              <h2 className="text-lg font-bold text-gray-900">采购进度</h2>
            </div>
            <div className="p-5">
              <div className="flex items-center">
                {timelineSteps.map((step: any, index: number) => (
                  <React.Fragment key={step.key}>
                    <div className="flex flex-col items-center">
                      <div
                        className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-sm ${
                          step.status === 'completed'
                            ? 'bg-green-500'
                            : step.current
                            ? 'bg-purple-500 animate-pulse'
                            : 'bg-gray-200'
                        }`}
                      >
                        {step.icon === '✓' ? (
                          <span className="text-sm">{step.icon}</span>
                        ) : (
                          <span className="text-sm">{step.icon}</span>
                        )}
                      </div>
                      <span
                        className={`text-xs mt-2 ${
                          step.status === 'current' ? 'text-purple-600 font-medium' : 'text-gray-600'
                        }`}
                      >
                        {step.label}
                      </span>
                    </div>
                    {index < timelineSteps.length - 1 && (
                      <div
                        className={`flex-1 h-1 mx-2 ${
                          step.status === 'completed' || step.status === 'current'
                            ? 'bg-purple-500'
                            : 'bg-gray-200'
                        }`}
                      ></div>
                    )}
                  </React.Fragment>
                ))}
              </div>
            </div>
          </div>

          {/* Related Work Order */}
          {purchaseOrder.workOrderNo && (
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100">
              <div className="p-5 border-b border-gray-100">
                <h2 className="text-lg font-bold text-gray-900">关联工单</h2>
              </div>
              <div className="p-5">
                <div className="flex items-center justify-between p-4 bg-blue-50 rounded-xl">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-blue-500 rounded-lg flex items-center justify-center text-white">
                      <FileText className="w-6 h-6" />
                    </div>
                    <div>
                      <div className="font-bold text-gray-900">{purchaseOrder.workOrderNo}</div>
                      <div className="text-sm text-gray-600">关联采购单</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Parts List */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100">
            <div className="p-5 border-b border-gray-100 flex items-center justify-between">
              <h2 className="text-lg font-bold text-gray-900">采购配件</h2>
              <span className="text-sm text-gray-500">共 {purchaseOrder.items.length} 项</span>
            </div>
            <div className="p-5">
              <table className="w-full">
                <thead>
                  <tr className="text-left text-sm text-gray-500 border-b border-gray-100">
                    <th className="pb-3 font-medium">配件信息</th>
                    <th className="pb-3 font-medium">规格型号</th>
                    <th className="pb-3 font-medium text-center">采购数量</th>
                    <th className="pb-3 font-medium">已收货</th>
                    <th className="pb-3 font-medium">单位</th>
                  </tr>
                </thead>
                <tbody className="text-sm">
                  {purchaseOrder.items.map((item, index) => {
                    const emojis = ['⚙️', '🛢️', '🔧', '📦', '💡']
                    const icon = emojis[index % emojis.length]

                    return (
                      <tr key={item.id} className="border-b border-gray-100">
                        <td className="py-4">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center text-xl">
                              {icon}
                            </div>
                            <div>
                              <div className="font-medium text-gray-900">{item.partName}</div>
                              <div className="text-xs text-gray-400">{item.skuCode}</div>
                            </div>
                          </div>
                        </td>
                        <td className="py-4 text-gray-600">{item.specification || '-'}</td>
                        <td className="py-4 text-center">
                          <span className="font-medium">{item.quantity}</span>
                        </td>
                        <td className="py-4 text-center">
                          <span className={item.receivedQty > 0 ? 'text-green-600' : 'text-gray-400'}>
                            {item.receivedQty}
                          </span>
                        </td>
                        <td className="py-4 text-gray-600">{item.unit}</td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Remark */}
          {purchaseOrder.remark && (
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100">
              <div className="p-5 border-b border-gray-100">
                <h2 className="text-lg font-bold text-gray-900">备注信息</h2>
              </div>
              <div className="p-5">
                <p className="text-sm text-gray-600">{purchaseOrder.remark}</p>
              </div>
            </div>
          )}
        </div>

        {/* Right: Sidebar */}
        <div className="space-y-6">
          {/* Quick Actions */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100">
            <div className="p-5 border-b border-gray-100">
              <h2 className="font-bold text-gray-900">快捷操作</h2>
            </div>
            <div className="p-5 space-y-2">
              {purchaseOrder.status === 'IN_TRANSIT' && purchaseOrder.trackingNo && (
                <Link
                  to={`/supply-chain/purchase/${purchaseOrder.id}/logistics`}
                  className="block px-4 py-3 bg-purple-50 hover:bg-purple-100 rounded-xl text-sm text-purple-700 text-center font-medium transition"
                >
                  <Truck className="w-4 h-4 inline mr-2" />
                  查看物流
                </Link>
              )}
              <button className="w-full px-4 py-3 bg-gray-50 hover:bg-gray-100 rounded-xl text-sm text-gray-700 text-left flex items-center gap-3 transition">
                <Download className="w-5 h-5 text-gray-500" />
                导出采购单
              </button>
              <button className="w-full px-4 py-3 bg-gray-50 hover:bg-gray-100 rounded-xl text-sm text-gray-700 text-left flex items-center gap-3 transition">
                <RefreshCw className="w-5 h-5 text-gray-500" />
                刷新数据
              </button>
            </div>
          </div>

          {/* Supplier Info */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100">
            <div className="p-5 border-b border-gray-100">
              <h2 className="font-bold text-gray-900">供应商信息</h2>
            </div>
            <div className="p-5">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                  <Building className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <div className="font-bold text-gray-900">{purchaseOrder.supplierName}</div>
                  <div className="text-xs text-gray-500">供应商</div>
                </div>
              </div>
            </div>
          </div>

          {/* Logistics Info */}
          {purchaseOrder.trackingNo && (
            <div className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-2xl border border-purple-200 p-5">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-purple-500 rounded-lg flex items-center justify-center">
                  <Truck className="w-5 h-5 text-white" />
                </div>
                <div>
                  <div className="font-bold text-gray-900">物流信息</div>
                  <div className="text-xs text-gray-500">物流跟踪</div>
                </div>
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">物流单号</span>
                  <span className="font-medium text-gray-900">{purchaseOrder.trackingNo}</span>
                </div>
              </div>
              <Link
                to={`/supply-chain/purchase/${purchaseOrder.id}/logistics`}
                className="mt-4 block w-full py-2 bg-purple-500 text-white text-center rounded-lg text-sm font-medium hover:bg-purple-600 transition"
              >
                查看物流详情
                <ChevronRight className="w-4 h-4 inline ml-1" />
              </Link>
            </div>
          )}

          {/* Timestamp Info */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100">
            <div className="p-5 border-b border-gray-100">
              <h2 className="font-bold text-gray-900">时间信息</h2>
            </div>
            <div className="p-5 space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">创建时间</span>
                <span className="font-medium text-gray-900">
                  {new Date(purchaseOrder.createdAt).toLocaleString('zh-CN')}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">最近状态</span>
                <span className="font-medium text-gray-900">
                  {statusMap[purchaseOrder.status as PurchaseOrderStatus]?.label || purchaseOrder.status}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default PurchaseDetail
